import imageCompression from "browser-image-compression";

/** Formats worth re-encoding. GIFs would lose their animation and SVGs are
 *  already tiny, so both pass through untouched. */
const CONVERTIBLE = ["image/jpeg", "image/png", "image/webp"];

const asWebpName = (name: string) =>
  `${name.replace(/\.[^.]+$/, "") || "photo"}.webp`;

/**
 * Prepares a photo for upload: WebP, at most 2000px on its long side.
 *
 * Photos are shown as banners, tiles and thumbnails — never at camera
 * resolution — and a 20-megapixel JPEG is several megabytes that every
 * listing showing it has to download. WebP is a quarter to a third smaller
 * than JPEG at the same quality (far more against PNG), so every photo is
 * re-encoded, not just the large ones. Documents pass through untouched, as
 * does anything the browser cannot encode, or that would come out larger.
 */
export async function shrinkPhoto(file: File): Promise<File> {
  if (!CONVERTIBLE.includes(file.type)) return file;
  try {
    const out = await imageCompression(file, {
      maxWidthOrHeight: 2000,
      maxSizeMB: 1.2,
      fileType: "image/webp",
      initialQuality: 0.82,
      useWebWorker: true,
    });
    // A browser that cannot write WebP hands back another format.
    if (out.type !== "image/webp") return file;
    // Already a small WebP: re-encoding only cost quality.
    if (file.type === "image/webp" && out.size >= file.size) return file;
    return new File([out], asWebpName(file.name), { type: "image/webp" });
  } catch {
    return file;
  }
}
